import {Component, Inject, Input, LOCALE_ID} from '@angular/core';
import {actionStatusesById, MessageTranslateService, OldTicketPriorityId, ticketBenefitsById, TicketChangeHistory, TicketChangeHistoryField, TicketFaultStatus, TicketFieldId, TicketPriority, TicketStatusId, TicketType} from "@shared/services";
import {SiteInfo} from "@shared/services/api/access-sites.interface";
import {AppConstants} from "@shared/util/app-constants";
import moment, {Moment} from 'moment-timezone';
import {HtmlUtils} from "@shared/util/general/html-utils";
import {getValue, TranslateParser} from "@ngx-translate/core";
import {formatCurrency, getCurrencySymbol} from "@angular/common";
import {ImpactId, PriorityId} from "@shared/api/model/type-ids";
import {Equipment} from "@app/graphql-typings/graphql";
import {CountryAndTimezoneService} from "@shared/services/country-timezone/country-and-timezone.service";
import {Impact, Priority} from "@app/layout/rules/template-editor/api/template";
import {DurationPipe} from "@shared/pipes/duration.pipe";


enum ChangeHistoryFieldType {
  CREATE_CHANGE_FIELD,
  SELECT_CHANGE_FIELD,
  DATE_CHANGE_FIELD,
  ADD_REMOVE_CHANGE_FIELD,
  TEXT_CHANGE_FIELD,
  ARCHIVED_CHANGE_FIELD,
  LINK_UNLINK_CHANGE_FIELD,
  FAULT_STATUS_CHANGE_FIELD,
  LAST_FAULT_CHANGE_DATE_FIELD,
  STATUS_CHANGE_FIELD,
  SAVINGS_CHANGE_FIELD,
  INBOX_CHANGE_FIELD,
  IMPACTS_CHANGE_FIELD,
  PRIORITIES_CHANGE_FIELD,
  TIME_TAKEN_FIELD
}


interface AttachmentChangeHistory {
  file_name: string;
  attachment_id: string;
}

interface AssigneeChangeHistory {
  user_id: string;
  full_name: string;
}


@Component({
  selector: 'app-ticket-change-history',
  templateUrl: './ticket-change-history.component.html',
  styleUrl: './ticket-change-history.component.scss',
  providers: [DurationPipe],
  standalone: false
})
export class TicketChangeHistoryComponent {
  private readonly TIMESTAMP_FORMAT_SHORT = AppConstants.TIME_FORMAT_MINUTE_PRECISION;

  @Input() site: SiteInfo;
  @Input() ticketType: TicketType;
  @Input() changeHistory: TicketChangeHistory;
  @Input() equipmentLoading = false;
  @Input() equipmentById: Map<number, Equipment>;
  @Input() impactsLoading = true;
  @Input() impactsById: Map<ImpactId, Impact>;
  @Input() prioritiesLoading = false;
  @Input() prioritiesById: Map<PriorityId, Priority>;
  @Input() oldActionPrioritiesById: Map<OldTicketPriorityId, TicketPriority>;
  @Input() actionPriorityUpgradeStart: Moment;
  @Input() actionPriorityUpgradeEnd: Moment;

  readonly DEFAULT_LOGO_CONTAINER_WIDTH = AppConstants.DEFAULT_LOGO_CONTAINER_WIDTH;
  readonly TicketFieldId = TicketFieldId;
  readonly ChangeHistoryFieldType = ChangeHistoryFieldType;
  readonly TIMESTAMP_FORMAT = AppConstants.TIME_FORMAT_SECOND_PRECISION;
  readonly NO_VALUE = '-';

  private changeFieldsByFieldName = new Map<string, ChangeHistoryFieldType>([
    [TicketFieldId.TICKET_ID, ChangeHistoryFieldType.CREATE_CHANGE_FIELD],
    [TicketFieldId.ASSIGNEES, ChangeHistoryFieldType.SELECT_CHANGE_FIELD],
    [TicketFieldId.PRIORITY_ID, ChangeHistoryFieldType.PRIORITIES_CHANGE_FIELD],
    [TicketFieldId.BENEFIT_IDS, ChangeHistoryFieldType.SELECT_CHANGE_FIELD],
    [TicketFieldId.STATUS_ID, ChangeHistoryFieldType.STATUS_CHANGE_FIELD],
    [TicketFieldId.INBOX, ChangeHistoryFieldType.INBOX_CHANGE_FIELD],
    [TicketFieldId.METADATA_CODE, ChangeHistoryFieldType.SELECT_CHANGE_FIELD],
    [TicketFieldId.SUMMARY, ChangeHistoryFieldType.TEXT_CHANGE_FIELD],
    [TicketFieldId.DESCRIPTION, ChangeHistoryFieldType.TEXT_CHANGE_FIELD],
    [TicketFieldId.SOLUTION, ChangeHistoryFieldType.TEXT_CHANGE_FIELD],
    [TicketFieldId.RESOLVED_AT, ChangeHistoryFieldType.DATE_CHANGE_FIELD],
    [TicketFieldId.UPDATED_AT, ChangeHistoryFieldType.DATE_CHANGE_FIELD],
    [TicketFieldId.CREATED_AT, ChangeHistoryFieldType.DATE_CHANGE_FIELD],
    [TicketFieldId.EQUIPMENT_IDS, ChangeHistoryFieldType.ADD_REMOVE_CHANGE_FIELD],
    [TicketFieldId.ATTACHMENTS, ChangeHistoryFieldType.ADD_REMOVE_CHANGE_FIELD],
    [TicketFieldId.ARCHIVED, ChangeHistoryFieldType.ARCHIVED_CHANGE_FIELD],
    [TicketFieldId.SOURCE_TICKETS, ChangeHistoryFieldType.LINK_UNLINK_CHANGE_FIELD],
    [TicketFieldId.TARGET_TICKETS, ChangeHistoryFieldType.LINK_UNLINK_CHANGE_FIELD],
    [TicketFieldId.FAULT_STATUS, ChangeHistoryFieldType.FAULT_STATUS_CHANGE_FIELD],
    [TicketFieldId.LAST_FAULT_TRANSITIONED_AT, ChangeHistoryFieldType.LAST_FAULT_CHANGE_DATE_FIELD],
    [TicketFieldId.COST_SAVINGS, ChangeHistoryFieldType.SAVINGS_CHANGE_FIELD],
    [TicketFieldId.IMPACT_IDS, ChangeHistoryFieldType.IMPACTS_CHANGE_FIELD],
    [TicketFieldId.TIME_TAKEN, ChangeHistoryFieldType.TIME_TAKEN_FIELD]
  ]);

  constructor(private parser: TranslateParser,
              private messageTranslateService: MessageTranslateService,
              private countryAndTimezoneService: CountryAndTimezoneService,
              private durationPipe: DurationPipe,
              @Inject(LOCALE_ID) private localeId: string) {
  }


  lookupChangeField(fieldName: string): ChangeHistoryFieldType {
    return this.changeFieldsByFieldName.get(fieldName);
  }

  hasNewLine(text: string): boolean {
    return !!text?.includes('\n');
  }

  getTranslatedDefaultErrorMessage(field: TicketChangeHistoryField | null): string {
    const oldValue = field?.old_value['value'] != null ? field?.old_value['value'] : this.NO_VALUE;
    const newValue = field?.new_value['value'] != null ? field?.new_value['value'] : this.NO_VALUE;
    return this.translateChangeHistoryMessage('Change History Unknown Field', field?.field_id, oldValue, newValue);
  }

  getTicketCreatedValue(): string {
    let typeName: string;
    switch (this.ticketType) {
      case TicketType.ESCALATED:
        typeName = 'Action';
        break;
      case TicketType.ALERT:
        typeName = 'Alert';
        break;
      default:
        throw new Error('Unknown ticket type ' + this.ticketType);
    }

    return HtmlUtils.escapeHTML(this.messageTranslateService.instant('Created the ticketType', {
      ticketType: this.messageTranslateService.instant(typeName)
    }));
  }

  getSelectFieldChangeValues(field: TicketChangeHistoryField): string {
    let msg = 'Change History Updated Field';
    const oldValue = field.old_value['value'];
    const newValue = field.new_value['value'];

    let fieldNameMessageKey: string;
    let translatedOldValue: string;
    let translatedNewValue: string;

    switch (field?.field_id) {
      case TicketFieldId.INBOX:
        fieldNameMessageKey = null;
        msg = 'Change History Moved Action';
        translatedOldValue = this.messageTranslateService.instant(this.getInboxMessageKey(field.old_value['value']));
        translatedNewValue = this.messageTranslateService.instant(this.getInboxMessageKey(field.new_value['value']));
        break;
      case TicketFieldId.ASSIGNEES:
        fieldNameMessageKey = 'Assignee';
        translatedOldValue = this.messageTranslateService.instant(this.getAssignee(oldValue));
        translatedNewValue = this.messageTranslateService.instant(this.getAssignee(newValue));
        break;
      case TicketFieldId.PRIORITY_ID:
        fieldNameMessageKey = 'Priority';
        translatedOldValue = this.messageTranslateService.instant(this.getPriorityName(oldValue, true));
        translatedNewValue = this.messageTranslateService.instant(this.getPriorityName(newValue));
        break;
      case TicketFieldId.IMPACT_IDS:
        fieldNameMessageKey = 'Impact';
        translatedOldValue = this.messageTranslateService.instant(this.getImpactNames(oldValue));
        translatedNewValue = this.messageTranslateService.instant(this.getImpactNames(newValue));
        break;
      case TicketFieldId.BENEFIT_IDS:
        fieldNameMessageKey = 'Impact';
        translatedOldValue = this.messageTranslateService.instant(this.getBenefitNames(oldValue));
        translatedNewValue = this.messageTranslateService.instant(this.getBenefitNames(newValue));
        break;
      case TicketFieldId.STATUS_ID:
        fieldNameMessageKey = 'Status';
        translatedOldValue = this.messageTranslateService.instant(this.getStatusDisplayText(oldValue));
        translatedNewValue = this.messageTranslateService.instant(this.getStatusDisplayText(newValue));
        break;
      case TicketFieldId.METADATA_CODE:
        fieldNameMessageKey = 'Metadata Code';
        translatedOldValue = oldValue || this.NO_VALUE;
        translatedNewValue = newValue || this.NO_VALUE;
        break;
      case TicketFieldId.TIME_TAKEN:
        fieldNameMessageKey = 'Time Spent';
        translatedOldValue = this.getTimeTakenDisplayText(oldValue);
        translatedNewValue = this.getTimeTakenDisplayText(newValue);
        break;
      default:
        return this.getTranslatedDefaultErrorMessage(field);
    }

    return this.translateChangeHistoryMessage(msg, fieldNameMessageKey, translatedOldValue, translatedNewValue);
  }

  private getAlertStatusFieldChangeValues(field: TicketChangeHistoryField): string {
    const oldStatusId: number = field.old_value['value'];
    const newStatusId: number = field.new_value['value'];
    let messageKey: string;
    let statusActionMessageKey: string;
    if (newStatusId === TicketStatusId.STATUS_NOT_DOING) {
      messageKey = 'statusActioned the Alert';
      statusActionMessageKey = 'Ignored';
    } else if (newStatusId === TicketStatusId.STATUS_RESOLVED) {
      messageKey = 'Alert statusActioned';
      statusActionMessageKey = 'Closed';
    } else if (newStatusId === TicketStatusId.STATUS_NEW) {
      if (oldStatusId === TicketStatusId.STATUS_NOT_DOING) {
        messageKey = 'statusActioned from Alert';
        statusActionMessageKey = 'Removed Ignore';
      } else if (oldStatusId === TicketStatusId.STATUS_RESOLVED) {
        messageKey = 'Alert statusActioned';
        statusActionMessageKey = 'Reopened';
      }
    }
    return messageKey != null
      ? this.interpolateMessageWithParams(messageKey, {
        statusActioned: this.getValueStrongHTML(this.messageTranslateService.instant(statusActionMessageKey), null)
      })
      : this.getSelectFieldChangeValues(field);
  }

  getStatusFieldChangeValues(field: TicketChangeHistoryField): string {
    return this.ticketType === TicketType.ALERT
      ? this.getAlertStatusFieldChangeValues(field)
      : this.getSelectFieldChangeValues(field);
  }

  getDateFieldChangeValues(field: TicketChangeHistoryField): string {
    const msg = 'Change History Updated Field';
    const oldValue = field.old_value['value'];
    const newValue = field.new_value['value'];
    const translatedOldValue = this.getDate(oldValue);
    const oldValueTitle = this.getDateTitle(oldValue);
    const translatedNewValue = this.getDate(newValue);
    const newValueTitle = this.getDateTitle(newValue);

    let fieldNameMessageKey: string;

    switch (field?.field_id) {
      case TicketFieldId.CREATED_AT:
        fieldNameMessageKey = 'Created Date';
        break;
      case TicketFieldId.RESOLVED_AT:
        fieldNameMessageKey = 'Resolved Date';
        break;
      case TicketFieldId.UPDATED_AT:
        fieldNameMessageKey = 'Updated Date';
        break;
      default:
        return this.getTranslatedDefaultErrorMessage(field);
    }

    return this.translateChangeHistoryMessage(msg, fieldNameMessageKey, translatedOldValue, translatedNewValue, oldValueTitle, newValueTitle);
  }

  getArchivedFieldChangeValues(field: TicketChangeHistoryField): string {
    switch (field?.field_id) {
      case TicketFieldId.ARCHIVED:
        return HtmlUtils.escapeHTML(this.messageTranslateService.instant(field.new_value['value'] ? 'Ticket archived' : 'Ticket unarchived'));
      default:
        return this.getTranslatedDefaultErrorMessage(field);
    }
  }

  getAddRemoveFieldChangeValues(field: TicketChangeHistoryField): string {
    const oldValues = field.old_value['value'] || [];
    const newValues = field.new_value['value'] || [];

    let fieldName: string;
    let removedValues: Array<any>;
    let addedValues: Array<any>;
    let messageKey: string;
    let resolvedOldValue: string;
    let resolvedNewValue: string;

    switch (field?.field_id) {
      case TicketFieldId.EQUIPMENT_IDS:
        fieldName = 'Equipment';
        removedValues = this.getDifference<number>(oldValues, newValues);
        addedValues = this.getDifference<number>(newValues, oldValues);
        resolvedOldValue = this.getEquipmentNames(removedValues);
        resolvedNewValue = this.getEquipmentNames(addedValues);
        break;
      case TicketFieldId.ATTACHMENTS:
        fieldName = 'Attachments';
        const oldAttachmentIds = this.getAttachmentIds(oldValues);
        const newAttachmentIds = this.getAttachmentIds(newValues);
        removedValues = this.getDifference<string>(oldAttachmentIds, newAttachmentIds);
        addedValues = this.getDifference<string>(newAttachmentIds, oldAttachmentIds);
        const fileNameById = oldValues.concat(newValues).reduce((acc: Map<string, string>, a: AttachmentChangeHistory) => {
          return acc.set(a.attachment_id, a.file_name);
        }, new Map<string, string>());
        const getFileNames = (ids: string[]) => ids.map(a => fileNameById.get(a)).join(', ');
        resolvedOldValue = getFileNames(removedValues);
        resolvedNewValue = getFileNames(addedValues);
        break;
      case TicketFieldId.IMPACT_IDS:
        fieldName = 'Impacts';
        removedValues = this.getDifference<string>(oldValues, newValues);
        addedValues = this.getDifference<string>(newValues, oldValues);
        resolvedOldValue = this.getImpactNames(removedValues);
        resolvedNewValue = this.getImpactNames(addedValues);
        break;
      case TicketFieldId.TIME_TAKEN:
        fieldName = 'Time Spent';
        removedValues = oldValues;
        addedValues = newValues;
        resolvedOldValue = oldValues ? this.getTimeTakenDisplayText(oldValues) : null;
        resolvedNewValue = newValues ? this.getTimeTakenDisplayText(newValues) : null;
        break;
      default:
        return this.getTranslatedDefaultErrorMessage(field);
    }

    if (addedValues.length && removedValues.length) {
      messageKey = 'Change History Field added and removed';
    } else if (addedValues.length) {
      messageKey = 'Change History Field added';
    } else {
      messageKey = 'Change History Field removed';
    }

    return this.translateChangeHistoryMessage(messageKey, fieldName, resolvedOldValue, resolvedNewValue);
  }

  getTextFieldChangeHistoryFirstMessage(field: TicketChangeHistoryField): string {
    let fieldName: string;

    switch (field?.field_id) {
      case TicketFieldId.SUMMARY:
        fieldName = 'Title';
        break;
      case TicketFieldId.DESCRIPTION:
        fieldName = 'Description';
        break;
      case TicketFieldId.SOLUTION:
        fieldName = 'Solution';
        break;
      case TicketFieldId.TIME_TAKEN:
        fieldName = 'Time Spent';
        break;
      default:
        throw new Error('Unknown Text Field ' + field?.field_id);
    }

    return this.translateChangeHistoryMessage('Change History Text Field First Message', fieldName, null, null);
  }

  getLinkUnlinkChangeHistoryValues(field: TicketChangeHistoryField): string {
    let fieldName: string;
    let messageKey: string;

    switch (field?.field_id) {
      case TicketFieldId.SOURCE_TICKETS:
        fieldName = 'Alert';
        break;
      case TicketFieldId.TARGET_TICKETS:
        fieldName = 'Action';
        break;
      default:
        return this.getTranslatedDefaultErrorMessage(field);
    }

    const { resolvedOldValue, resolvedNewValue } = this.getLinkUnlinkedDetails(field);

    if (resolvedNewValue.length && resolvedOldValue.length) {
      messageKey = 'Change History Field linked to and unlinked from';
    } else if (resolvedNewValue.length) {
      messageKey = 'Change History Field linked to';
    } else {
      messageKey = 'Change History Field unlinked from';
    }

    return this.translateChangeHistoryMessage(messageKey, fieldName, resolvedOldValue, resolvedNewValue);
  }

  getFaultStatusFieldChangeHistoryValues(field: TicketChangeHistoryField): string {
    const newFaultStatus = field.new_value['value'];
    let messageKey: string;
    let faultStatusText: string;
    switch (newFaultStatus) {
      case TicketFaultStatus.FAULT:
        messageKey = 'Alert Status in faultStatus';
        faultStatusText = 'Fault';
        break;
      case TicketFaultStatus.RECOVERED:
        messageKey = 'Alert Status faultStatus';
        faultStatusText = 'Recovered';
        break;
      default:
        return this.getTranslatedDefaultErrorMessage(field);
    }

    const faultStatusTranslated = this.messageTranslateService.instant(faultStatusText);
    return this.interpolateMessageWithParams(messageKey, {
      faultStatus: this.getValueStrongHTML(faultStatusTranslated, null)
    });
  }

  getLastFaultTransitionedFieldChangeValues(field: TicketChangeHistoryField): string {
    const lastFaultTransitionedAt = field.new_value['value'];
    const title = this.getDateTitle(lastFaultTransitionedAt);
    const dateText = this.getDate(lastFaultTransitionedAt);
    return this.interpolateMessageWithParams('Fault Changed at', {
      faultChangedAt: this.getValueStrongHTML(dateText, title)
    });
  }

  getSavingsFieldChangeValues(field: TicketChangeHistoryField): string {
    const oldSavings = field.old_value['value'];
    const oldSavingsCurrency = oldSavings != null ? this.formatSiteCurrency(oldSavings) : null;
    const newSavings = field.new_value['value'];
    const newSavingsCurrency = newSavings != null ? this.formatSiteCurrency(newSavings) : null;

    let messageKey: string;
    const fieldNameMessageKey = 'Cost Savings';

    if (oldSavings == null) {
      messageKey = 'Change History Field added';
    } else if (newSavings == null) {
      messageKey = 'Change History Field removed';
    } else {
      messageKey = 'Change History Updated Field';
    }

    return this.translateChangeHistoryMessage(messageKey, fieldNameMessageKey, oldSavingsCurrency, newSavingsCurrency);
  }

  getInboxChangeHistoryValues(field: TicketChangeHistoryField): string {
    if (this.ticketType === TicketType.ALERT) {
      const newInboxValue: boolean = field.new_value['value'];
      const messageKey = newInboxValue
        ? 'Marked the Alert as Unseen'
        : 'Marked the Alert as Seen';
      return this.messageTranslateService.instant({messageKey});
    }
    return this.getSelectFieldChangeValues(field);
  }

  getImpactsFieldChangeValues(field: TicketChangeHistoryField): string {
    const oldValue = field.old_value?.['value'];
    const newValue = field.new_value?.['value'];
    if (oldValue?.length > 1 || newValue?.length > 1) {
      return this.getAddRemoveFieldChangeValues(field);
    }
    return this.getSelectFieldChangeValues(field);
  }

  getTimeTakenFieldChangeValues(field: TicketChangeHistoryField): string {
    const oldValue = field.old_value?.['value'];
    const newValue = field.new_value?.['value'];
    if (oldValue && newValue) {
      return this.getSelectFieldChangeValues(field);
    } else if (oldValue || newValue) {
      return this.getAddRemoveFieldChangeValues(field);
    }
    return this.getSelectFieldChangeValues(field);
  }

  private getLinkUnlinkedDetails(field: TicketChangeHistoryField): { resolvedOldValue: string, resolvedNewValue: string } {
    const oldValues = field.old_value['value'] || [];
    const newValues = field.new_value['value'] || [];

    const oldAlertIds: string[] = oldValues.map((v: object) => v['ticket_id']);
    const newAlertIds: string[] = newValues.map((v: object) => v['ticket_id']);
    const removedValues = this.getDifference(oldAlertIds, newAlertIds);
    const addedValues = this.getDifference(newAlertIds, oldAlertIds);
    const summaryById = oldValues.concat(newValues).reduce((acc: Map<string, string>, v: object) => {
      return acc.set(v['ticket_id'], v['summary']);
    }, new Map<string, string>());

    return {
      resolvedOldValue: removedValues.map(id => summaryById.get(id)).join(', '),
      resolvedNewValue: addedValues.map(id => summaryById.get(id)).join(', ')
    };
  }

  private formatSiteCurrency(value: number): string {
    const currencyCode = this.countryAndTimezoneService.getCurrencyCode(this.site.monetary_currency);
    return formatCurrency(value, this.localeId, getCurrencySymbol(currencyCode, 'wide'), currencyCode);
  }



  private getDifference<T>(first: T[], second: T[]): T[] {
    if (!first) {
      return [];
    } else if (!second) {
      return first;
    } else {
      const toRemove = new Set(second);
      return first.filter(e => !toRemove.has(e));
    }
  }

  private getEquipmentNames(equipmentIds: number[]): string {
    return equipmentIds
      ? equipmentIds.map(id => this.equipmentById.get(id)?.name || id).join(', ')
      : this.NO_VALUE;
  }

  private getImpactNames(impactIds: ImpactId[]): string {
    return impactIds?.length
      ? impactIds.map(id => this.impactsById.get(id)?.name || id).join(', ')
      : this.NO_VALUE;
  }

  private getAttachmentIds(attachmentChangeHistory: AttachmentChangeHistory[]): string[] {
    return attachmentChangeHistory.map(a => a.attachment_id);
  }

  private getInboxMessageKey(inbox: boolean): string {
    return inbox ? 'Inbox' : 'Previous';
  }

  private getStatusDisplayText(statusId: number): string {
    return statusId
      ? actionStatusesById.get(statusId)?.display_text || String(statusId)
      : this.NO_VALUE;
  }

  private getTimeTakenDisplayText(durationString: string): string {
    return this.durationPipe.transform(moment.duration(durationString), {units: ['hour', 'minute']});
  }

  private getAssignee(assigneeChangeHistory: AssigneeChangeHistory[]): string {
    return assigneeChangeHistory && assigneeChangeHistory.length
      ? assigneeChangeHistory.map(a => a.full_name || a.user_id).join(', ')
      : 'Unassigned';
  }

  private getPriorityName(priorityId: number, isOldValue?: boolean): string {
    if (priorityId == null) {
      return this.NO_VALUE;
    } else {
      const changeHistoryTs = this.changeHistory.ts;
      let prioritiesById: Map<PriorityId, Priority> | Map<OldTicketPriorityId, TicketPriority>;
      if (changeHistoryTs.isBefore(this.actionPriorityUpgradeStart)) {
        prioritiesById = this.oldActionPrioritiesById;
      } else if (changeHistoryTs.isBefore(this.actionPriorityUpgradeEnd)) {
        prioritiesById = this.changeHistory.system
          ? (isOldValue ? this.oldActionPrioritiesById : this.prioritiesById)
          : this.oldActionPrioritiesById;
      } else {
        prioritiesById = this.prioritiesById;
      }
      return prioritiesById?.get(priorityId)?.name || String(priorityId);
    }
  }

  private getBenefitNames(benefitIds: number[]): string | null {
    return benefitIds
      ? benefitIds?.map((id: number) => ticketBenefitsById.get(id)?.benefit_name || String(id)).join(', ')
      : this.NO_VALUE;
  }

  private formatDateShort(theDate: moment.Moment): string | null {
    return theDate ? this.toSiteTimezone(theDate).format(this.TIMESTAMP_FORMAT_SHORT) : null;
  }

  private getDate(utcDateStr: string): string {
    return utcDateStr ? this.formatDateShort(moment.utc(utcDateStr)) : this.NO_VALUE;
  }

  private getDateTitle(utcDateStr: string): string {
    return utcDateStr ? this.formatDate(moment.utc(utcDateStr)) : null;
  }

  private formatDate(theDate: moment.Moment): string | null {
    return theDate ? this.toSiteTimezone(theDate).format(this.TIMESTAMP_FORMAT) : null;
  }

  private toSiteTimezone(theDate: moment.Moment): moment.Moment {
    return theDate
      ? this.site?.timezone
        ? theDate.clone().tz(this.site.timezone)
        : theDate
      : null;
  }

  private getTitleAttribute(titleText: string): string {
    return titleText ? ' title=\"' + HtmlUtils.escapeHTML(titleText) + '\"' : '';
  }

  private getValueStrongHTML(value: string, title: string): string {
    return '<strong' + this.getTitleAttribute(title) + '>' + HtmlUtils.escapeHTML(value) + '</strong>';
  }

  private getFieldNameEmphasisHTML(fieldNameMessageKey: string): string {
    return '<em>' + HtmlUtils.escapeHTML(this.messageTranslateService.instant(fieldNameMessageKey)) + '</em>';
  }

  private interpolateMessageWithParams(messageKey: string, params: object): string {
    const translations = this.messageTranslateService.translations[this.messageTranslateService.currentLang];
    const message = HtmlUtils.escapeHTML(getValue(translations, messageKey));
    return this.parser.interpolate(message, params);
  }

  private translateChangeHistoryMessage(messageKey: string,
                                        fieldNameMessageKey: string,
                                        translatedOldValue: string,
                                        translatedNewValue: string,
                                        oldValueTitle?: string,
                                        newValueTitle?: string): string {
    return this.interpolateMessageWithParams(messageKey, {
      ...fieldNameMessageKey && { fieldName: this.getFieldNameEmphasisHTML(fieldNameMessageKey) },
      ...(translatedOldValue != null) && { oldValue: this.getValueStrongHTML(translatedOldValue, oldValueTitle) },
      ...(translatedNewValue != null) && { newValue: this.getValueStrongHTML(translatedNewValue, newValueTitle) }
    });
  }

}
